package world.phantasmal.web.core

import world.phantasmal.web.externals.three.*
import kotlin.contracts.ExperimentalContracts
import kotlin.contracts.contract

private val tmpSphere = Sphere()

/**
 * Call after changing instance matrices or [InstancedMesh.count]. Uploads the matrices and drops
 * the cached instance bounds, which three.js (r151+) uses for frustum culling and raycasting.
 */
fun InstancedMesh.instancesChanged() {
    instanceMatrix.needsUpdate = true
    boundingBox = null
    boundingSphere = null
}

operator fun Vector3.plus(other: Vector3): Vector3 =
    clone().add(other)

operator fun Vector3.plusAssign(other: Vector3) {
    add(other)
}

operator fun Vector3.minus(other: Vector3): Vector3 =
    clone().sub(other)

operator fun Vector3.minusAssign(other: Vector3) {
    sub(other)
}

operator fun Vector3.times(scalar: Double): Vector3 =
    clone().multiplyScalar(scalar)

operator fun Vector3.timesAssign(scalar: Double) {
    multiplyScalar(scalar)
}

infix fun Vector3.dot(other: Vector3): Double =
    dot(other)

operator fun Quaternion.timesAssign(other: Quaternion) {
    multiply(other)
}

/**
 * Creates an [Euler] object from a [Quaternion] with the correct rotation order.
 */
fun Quaternion.toEuler(): Euler =
    Euler().setFromQuaternion(this, "ZXY")

/**
 * Creates an [Euler] object with the correct rotation order.
 */
fun euler(x: Float, y: Float, z: Float): Euler =
    euler(x.toDouble(), y.toDouble(), z.toDouble())

/**
 * Creates an [Euler] object with the correct rotation order.
 */
fun euler(x: Double, y: Double, z: Double): Euler =
    Euler(x, y, z, "ZXY")

/**
 * Creates an [Euler] object from a [Quaternion] with the correct rotation order.
 */
fun Euler.toQuaternion(): Quaternion =
    Quaternion().setFromEuler(this)

@Suppress("NOTHING_TO_INLINE")
inline fun Object3D.isMesh(): Boolean {
    contract {
        returns(true) implies (this@isMesh is Mesh)
    }

    return unsafeCast<Mesh>().isMesh
}

@Suppress("NOTHING_TO_INLINE")
inline fun Object3D.isSkinnedMesh(): Boolean {
    contract {
        returns(true) implies (this@isSkinnedMesh is SkinnedMesh)
    }

    return unsafeCast<SkinnedMesh>().isSkinnedMesh
}

fun boundingSphere(object3d: Object3D, bSphere: Sphere = Sphere()): Sphere {
    if (object3d.isMesh()) {
        val geom = object3d.geometry
        if (geom.boundingSphere == null) {
            geom.computeBoundingSphere()
        }

        // Don't use reference to union method to improve performance of emitted JS.
        geom.boundingSphere?.let {
            tmpSphere.copy(it)
            tmpSphere.applyMatrix4(object3d.matrixWorld)
            if (bSphere.radius < .0) {
                bSphere.copy(tmpSphere)
            } else {
                bSphere.union(tmpSphere)
            }
        }
    }

    object3d.children.forEach { boundingSphere(it, bSphere) }

    return bSphere
}
